import React from 'react';
import fhirpath from 'fhirpath';
import { FiEdit2, FiPrinter } from 'react-icons/fi';

import Information from '@/components/Information';
import { useAuth } from '@/features/auth/providers/AuthProvider/hooks';
import { useEnterpriseContext } from '@/features/enterprise/contexts/EnterpriseContext';

import IconButton from '@/components/IconButton';
import Markdown from '@/components/Markdown';
import Editor from '@/components/Editor';

import { FhirPedidoType } from '@/features/enterprise/models/types';
import { getSolicitacaoObrigatoriaMotivo } from '@/features/recursos/utils/solicitacaoObrigatoria';

import { useExecucaoContext } from '@/features/execucao/providers/ExecucaoProvider';
import Feature from '@/features/features/components/Feature';

import { clinicalDocumentPdf } from '@/features/pdf/documents/ClinicalDocumentLayout';
import DocumentPageOrientacao from '@/features/pdf/documents/DocumentPageOrientacao';

import OptionIntegracao from '@/types/OptionIntegracao';
import SolicitacoesAPI from '@/features/prescricao-livre/services/SolicitacoesAPI';
import {
  Container,
  Header,
  HeaderTitle,
  Actions,
  CollapsedContent,
  Description,
  TextSpan,
  DescriptionContainer,
  ButtonHover,
  CollapseButton,
} from './styles';

interface BoxOrientacaoEditavelProps {
  option: OptionIntegracao;
  selected?: boolean;
  disabled?: boolean;
}

interface EditorState {
  hoverContainer: boolean;
  status: null | 'disabled' | 'editing' | 'disabled-edited' | 'edited';
}

const BoxOrientacaoEditavel: React.FC<BoxOrientacaoEditavelProps> = ({
  option,
  selected,
  disabled,
}) => {
  const contentRef = React.createRef<HTMLDivElement>();

  const { user } = useAuth();
  const [{ encounter }] = useEnterpriseContext();

  const [printing, setPrinting] = React.useState(false);
  const solicitacaoObrigatoriaMotivo = React.useMemo(
    () =>
      getSolicitacaoObrigatoriaMotivo(
        'orientacao',
        option.solicitacao_obrigatoria_motivo,
      ),
    [option.solicitacao_obrigatoria_motivo],
  );

  const [contentHeight, setContentHeight] = React.useState<number>(0);
  const [isOpenedDetails, setIsOpenedDetails] = React.useState(false);
  const orientacao = React.useMemo(() => option.pedido, [option]);
  const [
    orientacaoEditavel,
    setOrientacaoEditavel,
  ] = React.useState<FhirPedidoType>(option.pedido);

  const titulo = React.useMemo(() => {
    return fhirpath.evaluate(orientacaoEditavel, 'CarePlan.title')[0];
  }, [orientacaoEditavel]);

  const texto = React.useMemo(() => orientacaoEditavel.text?.div || '', [
    orientacaoEditavel,
  ]);

  const hasDescription = React.useMemo(() => {
    return !!texto;
  }, [texto]);

  const [, { dispatchAlterarStatus }] = useExecucaoContext();

  const canEditOrientacao = React.useMemo(() => {
    return orientacao.status === 'draft' && !disabled;
  }, [orientacao, disabled]);

  const [editorState, setEditorState] = React.useState<EditorState>({
    hoverContainer: false,
    status: orientacao.reportedBoolean ? 'edited' : null,
  });

  const print = React.useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      try {
        setPrinting(true);
        await clinicalDocumentPdf(DocumentPageOrientacao, {
          user,
          encounter: encounter.data,
          resource: orientacaoEditavel as fhir4.CarePlan,
        });
      } catch (error) {
        throw new Error(`Erro ao gerar PDF da orientação médica: ${error}`);
      } finally {
        setPrinting(false);
      }
    },
    [user, encounter.data, orientacaoEditavel],
  );

  const toggleDetails = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      if (hasDescription) {
        setIsOpenedDetails(prev => {
          if (prev) {
            setEditorState(prevStateIndicacao => ({
              ...prevStateIndicacao,
              hoverContainer: false,
            }));
          }

          return !prev;
        });
      }
    },
    [hasDescription],
  );

  const dispatchCustomData = React.useCallback(
    async (pedido: FhirPedidoType) => {
      const retorno = await SolicitacoesAPI.update({
        id: option.value.toString(),
        tipo: 'orientacoes-medicas',
        resource: pedido as fhir4.CarePlan,
        opts: {
          session_id: option.sessionId,
        },
      });
      // const retorno = await IntegracaoService.updateFhirResource(pedido);

      if (retorno) {
        setEditorState(prev => ({
          ...prev,
          status: 'edited',
        }));

        setOrientacaoEditavel(retorno.pedido as FhirPedidoType);
      }
    },
    [option.sessionId, option.value],
  );

  const onCancelClick = React.useCallback(() => {
    setEditorState(prev => ({
      ...prev,
      status: orientacaoEditavel.reportedBoolean ? 'edited' : null,
      hoverContainer: false,
    }));
  }, [orientacaoEditavel]);

  const onConfirmClick = React.useCallback(
    (content: string) => {
      dispatchCustomData({
        ...orientacaoEditavel,
        ...{
          text: {
            div: content,
            status: 'additional',
          },
        },
      });
    },
    [dispatchCustomData, orientacaoEditavel],
  );

  const onChangeEditorText = React.useCallback(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [contentRef]);

  const onMouseOver = React.useCallback(() => {
    if (canEditOrientacao) {
      setEditorState(prev => ({ ...prev, hoverContainer: true }));
    }
  }, [canEditOrientacao]);

  const onMouseLeave = React.useCallback(() => {
    if (canEditOrientacao) {
      setEditorState(prev => ({ ...prev, hoverContainer: false }));
    }
  }, [canEditOrientacao]);

  const onEditClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
      if (canEditOrientacao) {
        if (selected) {
          event.stopPropagation();
        }
        setEditorState(prev => ({
          ...prev,
          status: 'editing',
          hoverContainer: false,
        }));
      }
    },
    [canEditOrientacao, selected],
  );

  const handleEditing = React.useCallback(() => {
    dispatchAlterarStatus({ status: 'on-hold' });
  }, [dispatchAlterarStatus]);

  const handleEdited = React.useCallback(() => {
    dispatchAlterarStatus({ status: 'active' });
  }, [dispatchAlterarStatus]);

  React.useEffect(() => {
    if (editorState.status === 'editing') {
      handleEditing();
    } else {
      handleEdited();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorState.status]);

  React.useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [contentRef]);

  return (
    <Container>
      <Header>
        <HeaderTitle highlight={isOpenedDetails}>{titulo}</HeaderTitle>

        <Actions>
          {(editorState.status === 'edited' ||
            editorState.status === 'disabled-edited') && (
            <TextSpan>editado</TextSpan>
          )}
          {isOpenedDetails && canEditOrientacao && (
            <ButtonHover onMouseOver={onMouseOver} onMouseLeave={onMouseLeave}>
              <IconButton
                tooltip="Editar Orientação"
                onClick={onEditClick}
                icon={<FiEdit2 />}
              />
            </ButtonHover>
          )}

          <Feature name="IMPRIMIR_ENCAMINHAMENTO">
            <IconButton
              tooltip="Imprimir"
              onClick={print}
              disabled={printing}
              icon={<FiPrinter />}
            />
          </Feature>

          {hasDescription && (
            <CollapseButton
              collapsed={isOpenedDetails}
              onClick={toggleDetails}
            />
          )}
        </Actions>
      </Header>

      {option.solicitacao_obrigatoria && (
        <Information type="warning" style={{ margin: '12px 0 0' }}>
          {solicitacaoObrigatoriaMotivo}
        </Information>
      )}

      <CollapsedContent
        ref={contentRef}
        style={{
          marginLeft: editorState.status === 'editing' ? -32 : 0,
          maxHeight: isOpenedDetails ? contentHeight : 0,
        }}
      >
        {texto && editorState.status === 'editing' ? (
          <Editor
            content={texto}
            onChange={onChangeEditorText}
            onCancelClick={onCancelClick}
            onConfirmClick={onConfirmClick}
          />
        ) : (
          <DescriptionContainer
            highlight={editorState.hoverContainer}
            disabled={disabled}
            selected={selected}
            onClick={onEditClick}
            onMouseOver={onMouseOver}
            onMouseLeave={onMouseLeave}
          >
            <Description>
              <span style={{ textOverflow: 'ellipsis' }}>
                <Markdown text={texto} />
              </span>
            </Description>
          </DescriptionContainer>
        )}
      </CollapsedContent>
    </Container>
  );
};

export default BoxOrientacaoEditavel;
